import { FREE_TRIAL_DAYS } from '../../../../constants/pricingTiers';
import { cn } from '@/lib/utils';

// How many days before the trial ends we email the reminder. Mirrors
// TRIAL_ENDING_NOTICE_LEAD_DAYS in ora-mailer's trialEndingNoticeService.ts —
// keep the two in sync so the "Day N reminder" copy matches when we actually send.
export const TRIAL_REMINDER_LEAD_DAYS = 3;

interface TrialTimelineProps {
  className?: string;
  trialPeriodDays?: number | null;
}

/**
 * Blinkist-style trial timeline: three steps left-to-right in a glass panel,
 * dots connected by a horizontal rail, day label and copy stacked beneath each
 * dot. The "Today" step is marked in teal (Ora's progress accent) with the
 * rail fading out of it toward the future steps. Plan-agnostic — rendered once
 * per surface (the Pricing page header and the unlock plan-select step).
 *
 * Both surfaces force the dark theme, so the muted white text is safe;
 * `className` allows per-surface tweaks (e.g. a max width).
 */
const TrialTimeline = ({ className, trialPeriodDays }: TrialTimelineProps) => {
  const effectiveTrialPeriodDays = trialPeriodDays ?? FREE_TRIAL_DAYS;
  const reminderDay = effectiveTrialPeriodDays - TRIAL_REMINDER_LEAD_DAYS;

  const rows = [
    { day: 'Today', text: <>Full access to Ora Pro, free</> },
    {
      day: `Day ${reminderDay}`,
      text: <>We email you a reminder before your trial ends</>,
    },
    {
      day: `Day ${effectiveTrialPeriodDays}`,
      text: (
        <>
          <span className="font-semibold text-white/90">
            First charge
            <br />
          </span>
          {'Cancel anytime before'}
        </>
      ),
    },
  ];

  return (
    <ol
      className={cn(
        'm-0! flex w-full! list-none rounded-2xl border border-white/10 bg-white/5 p-4 text-left backdrop-blur-xl sm:px-5',
        className
      )}
    >
      {rows.map((row, index) => (
        <li key={row.day} className="min-w-0 flex-1 text-xs text-white/50">
          <span className="mb-3 block pr-3 font-heading font-medium text-white">
            {row.day}
          </span>
          {/* Marker row: the dot plus the rail to the next step's dot. The row
              spans the full column width (the horizontal padding lives on the
              text rows instead), so the rail reaches the column edge and
              connects with the next dot. "Today" gets the teal progress
              treatment; future steps are hollow. After the last dot the rail
              continues through the panel padding and fades out, hinting at
              the timeline running on. */}
          <div aria-hidden="true" className="relative mb-3 h-2">
            <span
              className={cn(
                'absolute left-0 top-0 h-2 w-2 rounded-full',
                index === 0
                  ? 'bg-green-400 ring-4 ring-green-400/20'
                  : 'border border-white/30'
              )}
            />
            <span
              className={cn(
                'absolute top-[3.5px] h-px',
                index === 0
                  ? 'left-3 right-0 bg-gradient-to-r from-green-400/50 to-white/15'
                  : index < rows.length - 1
                    ? 'left-2 right-0 bg-white/15'
                    : '-right-4 left-2 bg-gradient-to-r from-white/15 to-transparent sm:-right-5'
              )}
            />
          </div>
          <span className="block pr-3 leading-snug">{row.text}</span>
        </li>
      ))}
    </ol>
  );
};

export default TrialTimeline;
